import React, { useEffect, useMemo, useState } from "react";
import ResetButton from "@/components/ResetButton";
import PromptBox from "@/components/PromptBox";
import useDraft from "@/lib/useDraft";
import { Move, Shuffle, Info } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import ModelPicker from "@/components/ModelPicker";
import GenerationGrid from "@/components/GenerationGrid";
import GenerateButton, { useCost } from "@/components/GenerateButton";
import UploadZone from "@/components/UploadZone";
import { Seg, Field } from "@/components/Seg";
import { useAttachments } from "@/lib/useUploader";

const TABS = [
  { id: "motion_transfer", label: "Motion Transfer", icon: Move, photo: "Add person photo", photoHint: "1–8 clear photos of the person who should appear" },
  { id: "object_swap", label: "Objects Swap", icon: Shuffle, photo: "Add replacement photos", photoHint: "1–8 photos of the person, product or object to swap in" },
];

export default function RemixStudio({ projectId, models, configured, items, references = [], onUploaded, generate, onOpen, onRetry, seed , header, onChanged, modelPick, onReset}) {
  const [tab, setTab] = useState("motion_transfer");
  const [model, setModel] = useState(null);
  const [usePrompt, setUsePrompt] = useState(false);
  const [prompt, setPrompt] = useDraft(`ax_draft_${projectId}_remix`);
  const [resolution, setResolution] = useState("480p");
  const [busy, setBusy] = useState(false);
  const video = useAttachments(projectId, 1, onUploaded, true, "remix-video");
  const photos = useAttachments(projectId, 8, onUploaded, true, "remix-photos");
  const meta = TABS.find((t) => t.id === tab);

  const pool = useMemo(() => models.filter((m) => m.section === "remix" && m.modes.includes(tab)), [models, tab]);
  useEffect(() => {
    setModel((cur) => (cur && pool.some((m) => m.id === cur.id) ? cur : pool.find((m) => m.available) || pool[0] || null));
  }, [pool]);
  const picked = React.useRef(null);
  useEffect(() => {
    const m = modelPick && models.find((x) => x.id === modelPick.id && x.section === "remix");
    if (!m) return;
    picked.current = m;
    if (!m.modes.includes(tab)) setTab(m.modes[0]); else setModel(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelPick, models]);
  useEffect(() => { if (picked.current && pool.some((x) => x.id === picked.current.id)) { setModel(picked.current); picked.current = null; } }, [pool]);
  useEffect(() => {
    if (!seed) return;
    if (seed.mode) setTab(seed.mode);
    setUsePrompt(!!seed.prompt);
    if (seed.prompt) setPrompt(seed.prompt);
    if (seed.settings?.resolution) setResolution(seed.settings.resolution);
    const pick = (ids) => (ids || []).map((id) => references.find((r) => r.id === id)).filter(Boolean);
    video.setItems(pick([seed.video_id]));
    photos.setItems(pick(seed.image_ids));
    const m = models.find((x) => x.id === seed.model_id && x.section === "remix");
    if (m) setTimeout(() => setModel(m), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  const caps = model?.capabilities || {};
  const okRes = (r) => (caps.resolutions || []).includes(r);
  const dur = video.items[0]?.duration;
  const durBad = dur && (dur < 4 || dur > 15);
  const settings = { duration: Math.ceil(dur || 4), ...(okRes(resolution) && { resolution }) };
  const usd = useCost(model?.available && model.id, settings);

  const reason = !configured ? "Coming Soon" : !model?.available ? "This model isn't available yet"
    : video.uploading || photos.uploading ? "Waiting for upload…" : !video.ids.length ? "Add a video to edit"
    : durBad ? "Video must be 4–15 seconds long"
    : usePrompt && prompt.length > (model?.max_prompt || 2000) ? `Prompt too long — max ${model?.max_prompt || 2000} characters` : !photos.ids.length ? `${meta.photo} (at least 1)` : "";

  const go = async () => {
    setBusy(true);
    await generate({ section: "remix", mode: tab, model_id: model.id, prompt: usePrompt ? prompt.replace(/@image_?(\d+)\b/g, "@image_$1").replace(/@video_?\d+\b/g, "@video") : "", settings,
      image_ids: photos.ids, video_id: video.ids[0], media_duration: dur });
    setBusy(false);
  };

  return (
    <div className="flex-1 flex min-h-0">
      <aside className="w-[380px] shrink-0 border-r border-white/5 bg-[#0B1013]/70 backdrop-blur-xl flex flex-col min-h-0" data-testid="remix-sidebar">
        <div className="px-3 pt-3 flex justify-end"><ResetButton onReset={onReset} testid="remix-panel-reset" /></div>
        <div className="p-3 grid grid-cols-2 gap-1 border-b border-zinc-800/80">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`remix-tab-${t.id}`}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-medium ${tab === t.id ? "bg-zinc-800 text-white" : "text-zinc-500 hover:text-white hover:bg-zinc-800/50"}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          <p className="text-xs text-zinc-500 leading-relaxed flex gap-2"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {tab === "motion_transfer" ? "Keeps every move, camera and timing of your video — but performed by the person in your photos." : "Replaces a person, product or object in your video with what's in your photos."}
          </p>
          <UploadZone testid="remix-video-zone" title="Add video to edit" badge="4–15 s" subtitle="MP4/MOV · the output keeps its length and framing"
            accept="video/*" max={1} items={video.items} onFiles={video.add} onRemove={video.remove} />
          {durBad && <p className="text-xs text-red-400 -mt-3" data-testid="remix-duration-error">This clip is {Math.round(dur)} s — use a 4–15 s video.</p>}
          <UploadZone testid="remix-photo-zone" title={meta.photo} badge="1–8 photos" subtitle={meta.photoHint}
            accept="image/*" max={8} items={photos.items} onFiles={photos.add} onRemove={photos.remove} />
          <div className="ax-panel p-3 space-y-3">
            <label className="flex items-center justify-between">
              <span className="text-sm">Add a prompt <span className="text-zinc-500 text-xs">(optional)</span></span>
              <Switch className="data-[state=checked]:bg-[#7AF25F]" checked={usePrompt} onCheckedChange={setUsePrompt} data-testid="remix-prompt-switch" />
            </label>
            {usePrompt && <PromptBox value={prompt} onChange={setPrompt} testid="remix-prompt" minRows={3} maxChars={model?.max_prompt || 2000} improve
              tags={[...video.items.map((r, i) => ({ tag: `video${i + 1}`, ref: r })), ...photos.items.map((r, i) => ({ tag: `image${i + 1}`, ref: r }))]}
              placeholder="Extra guidance, e.g. 'keep the red jacket'  ·  Type @ to tag attached media" className="ax-input" />}
          </div>
          <Field label="Model">
            <ModelPicker models={models} value={model?.id} onChange={setModel} configured={configured} testid="remix-model"
              groups={[{ label: "Remix Models", filter: (m) => m.section === "remix" && m.modes.includes(tab) }]} />
          </Field>
          {caps.resolutions?.length > 0 && <Seg label="Resolution" testid="remix-resolution" options={caps.resolutions} value={resolution} onChange={setResolution} isEnabled={okRes} />}
          {dur ? <p className="text-[11px] text-zinc-500">Billed for {Math.ceil(dur)} s — the length of your video.</p> : null}
        </div>
        <div className="p-4 border-t border-zinc-800/80">
          <GenerateButton usd={usd} onClick={go} busy={busy} disabledReason={reason} testid="remix-generate" confirm className="w-full" />
          {reason && <p className="text-[11px] text-zinc-500 text-center mt-2" data-testid="remix-generate-hint">{reason}</p>}
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto p-6" data-testid="remix-history">
        {header}
        <GenerationGrid items={items} onOpen={onOpen} onRetry={onRetry} onChanged={onChanged} cols="grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3"
          emptyTitle="No remixes yet" emptyHint="Upload a 4–15 s video and 1–8 photos on the left, then press Generate." />
      </main>
    </div>
  );
}
